import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Close';
import ContactHelpTip from '../Profiles/ProfileTabs/ContactsAndEthniicityTab/ContactHelpTip';
import CONTACT_HELP from '../Profiles/ProfileTabs/ContactsAndEthniicityTab/contactHelpText';
import {emailQuestion, phoneQuestion} from '../Profiles/ProfileTabs/ContactsAndEthniicityTab/addContactRules';
import {
    STAFF_CONTACT_STEPS,
    canGoOnStaff,
    emptyStaffContact,
    staffContactErrors,
    staffContactFrom,
    staffContactPayload,
    staffQuestionFor
} from './staffContactRules';

// Maria, 19 October 2026: the staff version of the student Add Contact box, in the same style, one page at a time.
const StaffAddContactBox = ({open, mode, existingContact, contactTypes, onSave, onClose}) => {
    const [contact, setContact] = useState(emptyStaffContact());
    const [step, setStep] = useState('who');
    const [touched, setTouched] = useState({});

    useEffect(() => {
        if (open) {
            setContact(mode === 'edit' && existingContact ? staffContactFrom(existingContact) : emptyStaffContact());
            setStep('who');
            setTouched({});
        }
    }, [open]);

    const stepNumber = STAFF_CONTACT_STEPS.indexOf(step) + 1;
    const set = (field, value) => setContact(prev => ({...prev, [field]: value}));
    const touch = field => setTouched(prev => ({...prev, [field]: true}));
    const errors = staffContactErrors(contact);
    const back = () => (stepNumber <= 1 ? onClose() : setStep(STAFF_CONTACT_STEPS[stepNumber - 2]));
    const next = () => setStep(STAFF_CONTACT_STEPS[stepNumber]);

    const textField = (field, label, required) => (
        <TextField
            size="medium"
            key={field}
            required={required}
            label={label}
            value={contact[field]}
            onChange={(e) => set(field, e.target.value)}
            onBlur={() => touch(field)}
            error={Boolean(touched[field] && errors[field])}
            helperText={touched[field] ? errors[field] : ''}
            inputProps={{'data-testid': `staff-contact-${field}`}}
        />
    );

    const checkRows = [
        ['Forename', contact.forename.trim()],
        ['Surname', contact.surname.trim()],
        ['Relation Type', contactTypes.find(t => t.code === contact.typeCode)?.description ?? ''],
        ['Email', contact.email.trim()],
        ['Phone Number', contact.phone.trim()]
    ];

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            onClose={onClose}
            data-testid="staff-contact-dialog"
            PaperProps={{sx: {borderRadius: 3, height: 'min(600px, calc(100% - 64px))'}}}
        >
            <Box sx={{px: 4, pt: 2, pb: 1.5, borderBottom: '1px solid #eef0f3'}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="overline" sx={{color: '#0DAD95', fontWeight: 700, letterSpacing: 1}}>
                        {mode === 'edit' ? 'Edit Contact Information' : 'Add New Contact'}
                    </Typography>
                    <IconButton size="small" onClick={onClose} aria-label="Close">
                        <CancelIcon fontSize="small"/>
                    </IconButton>
                </Stack>
                <Typography variant="h5" sx={{fontWeight: 600}} data-testid="staff-contact-question">
                    {staffQuestionFor(step, contact)}
                </Typography>
                <Stack direction="row" spacing={1} sx={{mt: 1.5}} data-testid="staff-contact-progress">
                    {STAFF_CONTACT_STEPS.map((stepName, index) => (
                        <Box key={stepName} sx={{flex: 1, height: 4, borderRadius: 2, backgroundColor: index < stepNumber ? '#186BBE' : '#e3e8ef'}}/>
                    ))}
                </Stack>
            </Box>

            <DialogContent sx={{px: 4, pt: 3, pb: 3, display: 'flex', flexDirection: 'column', justifyContent: ['who', 'contact'].includes(step) ? 'center' : 'flex-start'}}>
                {step === 'who' && (
                    <Stack spacing={2.5} sx={{width: '100%', maxWidth: 480, mx: 'auto'}}>
                        {textField('forename', 'Forename', true)}
                        {textField('surname', 'Surname', true)}
                        <TextField
                            size="medium"
                            select
                            label="Relation Type"
                            value={contact.typeCode}
                            onChange={(e) => set('typeCode', e.target.value)}
                            SelectProps={{SelectDisplayProps: {'data-testid': 'staff-contact-type'}}}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {contactTypes.map(t => <MenuItem key={t.code} value={t.code}>{t.description}</MenuItem>)}
                        </TextField>
                    </Stack>
                )}

                {step === 'contact' && (
                    // as on the student box: a question above each box, and no words in the boxes
                    <Stack spacing={2.5} sx={{width: '100%', maxWidth: 480, mx: 'auto'}}>
                        <Box>
                            <Typography component="label" htmlFor="staff-contact-email" sx={{display: 'block', fontWeight: 600, mb: 0.75}}>
                                {emailQuestion(contact)}
                            </Typography>
                            <TextField
                                size="medium"
                                fullWidth
                                id="staff-contact-email"
                                type="email"
                                value={contact.email}
                                onChange={(e) => set('email', e.target.value)}
                                onBlur={() => touch('email')}
                                error={Boolean(touched.email && errors.email)}
                                helperText={touched.email ? errors.email : ''}
                                inputProps={{'data-testid': 'staff-contact-email'}}
                            />
                        </Box>
                        <Box>
                            <Typography component="label" htmlFor="staff-contact-phone" sx={{display: 'block', fontWeight: 600, mb: 0.75}}>
                                <ContactHelpTip label={phoneQuestion(contact)} text={CONTACT_HELP.phone}/>
                            </Typography>
                            <TextField
                                size="medium"
                                fullWidth
                                id="staff-contact-phone"
                                value={contact.phone}
                                onChange={(e) => set('phone', e.target.value)}
                                onBlur={() => touch('phone')}
                                error={Boolean(touched.phone && errors.phone)}
                                helperText={touched.phone ? errors.phone : ''}
                                inputProps={{'data-testid': 'staff-contact-phone'}}
                            />
                        </Box>
                    </Stack>
                )}

                {step === 'check' && (
                    <Table size="small" data-testid="staff-contact-check">
                        <TableBody>
                            {checkRows.map(([label, value]) => (
                                <TableRow key={label}>
                                    <TableCell sx={{fontWeight: 600, width: '40%'}}>{label}</TableCell>
                                    <TableCell>{value}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>

            <Box sx={{px: 4, py: 2, borderTop: '1px solid #eef0f3'}}>
                <Stack direction="row" justifyContent="space-between">
                    <Button data-testid="staff-contact-back" variant="outlined" onClick={back}>Back</Button>
                    <Button
                        data-testid={step === 'check' ? 'staff-contact-save' : 'staff-contact-next'}
                        variant="contained"
                        onClick={step === 'check' ? () => onSave(staffContactPayload(contact)) : next}
                        disabled={!canGoOnStaff(step, contact)}
                        sx={{backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}}}
                    >
                        {step === 'check' ? 'Save' : 'Next'}
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
};

StaffAddContactBox.propTypes = {
    open: PropTypes.bool,
    mode: PropTypes.oneOf(['add', 'edit']),
    existingContact: PropTypes.object,
    contactTypes: PropTypes.array,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

StaffAddContactBox.defaultProps = {
    mode: 'add',
    contactTypes: []
};

export default StaffAddContactBox;
